<script>
  import { onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faPencil, faUserTag, faUserPlus, faBan, faCheck, faSearch, faCircleXmark, faEnvelope, faEllipsis, faHistory, faTrashCan } from '@fortawesome/pro-light-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import UserModal from '$lib/UserModal.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { formatToLocalDate } from '$utils/dateUtils.js';
  import { exportToExcel, generateExportFilename } from '$utils/export.js';
  import { selectedStudy } from '$stores/selectionStore';
  import { studyProperties } from '$stores/configStore';
  import { userRoles, userReadOnly } from '$stores/sessionStore';
  import { hasAuditLogAccess } from '$utils/permissionUtils.js';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import Footer from '$lib/Footer.svelte';
  import Pagination from '$lib/Pagination.svelte';
  
  // Use proxy API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let users = [];
  let resultCount = 0;
  let currentPage = 1;
  let pageSize = 20;
  let totalPages = 1;
  let sortField = 'lastName';
  let sortOrder = 'asc';

  // Filter and search state
  let searchText = '';
  let selectedCountry = '';
  let selectedUserType = '';
  let selectedRole = '';
  let selectedState = '';
  let countryOptions = [];

  // Modal state
  let showUserModal = false;
  let selectedUser = null;
  let isCreatingUser = false;
  
  // Assign workflow modal state
  let showAssignWorkflowModal = false;
  let workflows = [];
  let userForAssignment = null; // keep modal user separate from menu user
  let isLoadingAssignments = false;
  let isAssigning = false;
  let assignedMap = {}; // workflowId -> assignmentId
  let selectedWorkflowIds = new Set();
  
  // Confirmation modal state
  let showConfirmationModal = false;
  let confirmationMessage = '';
  let confirmationAction = null;
  let userToAction = null;
  
  // Loading state for password reset
  let isResendingPasswordReset = false;
  let isExporting = false;

  // Audit log modal state
  let showAuditLog = false;
  let auditLogUser = null;

  // Audit log permissions
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);

  // Menu state for user actions
  let showUserMenu = false;
  let selectedUserForMenu = null;
  let userMenuPosition = { top: 0, left: 0, direction: 'down' };

  // Role display mapping
  const roleDisplayNames = {
    'admin': 'Administrator',
    'read-only-b': 'Read Only Blinded',
    'read-only-u': 'Read Only Unblinded',
    'site': 'Site User',
    'study': 'Study User',
    'support': 'Support User'
  };

  // User type display mapping
  const userTypeDisplayNames = {
    'Internal': 'Internal',
    'Site': 'Site',
    'CRA': 'CRA',
    'Sponsor': 'Sponsor',
    'Support': 'Support'
  };

  onMount(async () => {
    await fetchAllUsersForCountries();
    fetchUsers();

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  async function fetchAllUsersForCountries() {
    try {
      // Use the new efficient endpoint to get unique countries
      const res = await fetch(`${API_BASE_URL}/users/countries`);
      if (res.ok) {
        const response = await res.json();
        countryOptions = response.data || [];
        console.log('Unique countries found:', countryOptions);
      }
    } catch (error) {
      console.error('Error fetching unique countries:', error);
    }
  }

  async function fetchUsers() {
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        pageSize,
        sortField,
        sortOrder
      });

      // Add search parameter
      if (searchText.trim()) {
        queryParams.append('searchText', searchText.trim());
      }

      // Add filter parameters
      if (selectedCountry) {
        queryParams.append('country', selectedCountry);
      }
      if (selectedUserType) {
        queryParams.append('userType', selectedUserType);
      }
      if (selectedRole) {
        queryParams.append('role', selectedRole);
      }
      if (selectedState) {
        queryParams.append('state', selectedState);
      }

      console.log('Fetching users with params:', {
        sortField,
        sortOrder,
        searchText: searchText.trim(),
        selectedCountry,
        selectedUserType,
        selectedRole,
        selectedState
      });

      const res = await fetch(
        `${API_BASE_URL}/users?${queryParams}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch users');
      }

      const response = await res.json();
      users = Array.isArray(response.data) ? response.data : [];
      resultCount = response.totalCount || 0;
      currentPage = response.page || 1;
      totalPages = response.totalPages || 1;
    } catch (err) {
      console.error('Error fetching users:', err);
      users = [];
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;

      const queryParams = new URLSearchParams({
        page: '1',
        pageSize: '999999',
        sortField,
        sortOrder
      });

      if (searchText.trim()) {
        queryParams.append('searchText', searchText.trim());
      }
      if (selectedCountry) {
        queryParams.append('country', selectedCountry);
      }
      if (selectedUserType) {
        queryParams.append('userType', selectedUserType);
      }
      if (selectedRole) {
        queryParams.append('role', selectedRole);
      }
      if (selectedState) {
        queryParams.append('state', selectedState);
      }

      const res = await fetch(`${API_BASE_URL}/users?${queryParams}`);
      if (!res.ok) {
        throw new Error('Failed to fetch users data for export');
      }

      const response = await res.json();
      const exportUsers = response.data || [];

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        reportType: 'Users'
      });

      const formattedData = exportUsers.map((user) => ({
        Sponsor: user.sponsor || '',
        Country: user.country || '',
        Type: formatUserTypeDisplay(user.userType),
        Name: formatFullName(user.firstName, user.lastName),
        Email: user.email || '',
        Role: user.roles && user.roles.length > 0 ? formatRoleDisplay(user.roles[0]) : '',
        'Last Login': user.lastLogin ? formatToLocalDate(user.lastLogin) : 'Never',
        State: user.state === 'active' ? 'Active' : user.state === 'pending' ? 'Pending' : user.state === 'inactive' ? 'Blocked' : 'Unknown'
      }));

      await exportToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting users to Excel:', error);
    } finally {
      isExporting = false;
    }
  }

  function handleRefresh() {
    fetchUsers();
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchUsers();
  }

  // Handle name sorting specifically
  function handleNameSort() {
    console.log('handleNameSort called, current sortField:', sortField, 'current sortOrder:', sortOrder);
    if (sortField === 'name') {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = 'name';
      sortOrder = 'asc';
    }
    console.log('After handleNameSort: sortField:', sortField, 'sortOrder:', sortOrder);
    fetchUsers();
  }

  // Get sort indicator for a column
  function getSortIndicator(field) {
    if (sortField !== field) return '';
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  }

  // Reactive sort indicators - force reactivity by including both variables
  $: sponsorSortIndicator = sortField === 'sponsor' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
  $: countrySortIndicator = sortField === 'country' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
  $: userTypeSortIndicator = sortField === 'userType' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
  $: nameSortIndicator = sortField === 'name' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
  $: emailSortIndicator = sortField === 'email' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
  $: lastLoginSortIndicator = sortField === 'lastLogin' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
  $: stateSortIndicator = sortField === 'state' ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';

  async function handleEditClick(user) {
    selectedUser = user;
    showUserModal = true;
    isCreatingUser = false;
  }

  function handleRoleClick(user) {
    selectedUser = user;
    showUserModal = true;
    isCreatingUser = false;
  }

  function handleInviteClick() {
    showUserModal = true;
    isCreatingUser = true;
  }

  function handleUserModalClose() {
    showUserModal = false;
    selectedUser = null;
  }

  function handlePageChange(page) {
    currentPage = page;
    fetchUsers();
  }

  function handleUserUpdated(event) {
    // Refresh the users list to reflect the changes
    fetchUsers();
    // Also refresh country options in case a new country was added
    fetchAllUsersForCountries();
  }

  function handleUserCreated(event) {
    // Refresh the users list to include the newly created user
    fetchUsers();
    // Also refresh country options in case a new country was added
    fetchAllUsersForCountries();
  }

  function handleBlockUser(user) {
    userToAction = user;
    confirmationMessage = `Are you sure you want to block ${user.firstName || user.email}? This will prevent them from logging in.`;
    confirmationAction = 'block';
    showConfirmationModal = true;
  }

  function handleUnblockUser(user) {
    userToAction = user;
    confirmationMessage = `Are you sure you want to unblock ${user.firstName || user.email}? This will allow them to log in again.`;
    confirmationAction = 'unblock';
    showConfirmationModal = true;
  }

  async function handleResendPasswordReset(user) {
    if (isResendingPasswordReset) return;
    
    try {
      isResendingPasswordReset = true;
      
      const res = await fetch(`${API_BASE_URL}/users/${user._id}/resend-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error || result.details || 'Failed to resend password reset email');
      }

      alert(`Password reset email sent successfully to ${user.email}`);
      
    } catch (err) {
      console.error('Error resending password reset email:', err);
      alert(`Error resending password reset email: ${err.message}`);
    } finally {
      isResendingPasswordReset = false;
    }
  }

  async function handleConfirmation() {
    if (!userToAction || !confirmationAction) return;

    try {
      const action = confirmationAction === 'block' ? 'block' : 'unblock';
      const res = await fetch(`${API_BASE_URL}/users/${userToAction._id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error || result.details || `Failed to ${action} user`);
      }

      // Refresh the users list to reflect the changes
      fetchUsers();
    } catch (err) {
      console.error(`Error ${confirmationAction}ing user:`, err);
      alert(`Error ${confirmationAction}ing user: ${err.message}`);
    }
  }

  function handleConfirmationClose() {
    showConfirmationModal = false;
    userToAction = null;
    confirmationAction = null;
    confirmationMessage = '';
  }
  
  // Format role display
  function formatRoleDisplay(role) {
    if (!role) return '';
    return roleDisplayNames[role] || role;
  }

  // Format user type display
  function formatUserTypeDisplay(userType) {
    if (!userType) return '';
    return userTypeDisplayNames[userType] || userType;
  }

  // Helper function to format first and last name
  function formatFullName(firstName, lastName) {
    if (!firstName && !lastName) return '-';
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return firstName || lastName;
  }

  function handleSearch() {
    currentPage = 1; // Reset to first page when searching
    fetchUsers();
  }

  function handleClearSearch() {
    searchText = '';
    currentPage = 1;
    fetchUsers();
  }

  function handleFilterChange() {
    currentPage = 1; // Reset to first page when filtering
    fetchUsers();
  }

  function handleClearFilters() {
    selectedCountry = '';
    selectedUserType = '';
    selectedRole = '';
    selectedState = '';
    currentPage = 1;
    fetchUsers();
  }

  function handleUserMenuClick(event, user) {
    event.stopPropagation();
    selectedUserForMenu = user;
    // QA-011: the dropdown uses position: fixed, which is viewport-relative.
    // getBoundingClientRect() is also viewport-relative. Do NOT add
    // window.scrollY/scrollX — that double-counts the scroll offset and pushes
    // the menu off-screen any time the page has been scrolled (also closes
    // QA-008, which was the same bug observed after sort-triggered re-render).
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 150; // Approximate, adjust as needed or measure dynamically
    const menuWidth = 192; // w-48 in px
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    let direction = 'down';
    let top = buttonRect.bottom;
    let left = buttonRect.left;
    // If not enough space below, flip up
    if (buttonRect.bottom + menuHeight > viewportHeight) {
      direction = 'up';
      top = buttonRect.top - menuHeight;
      if (top < 0) top = 0; // Clamp to top
    }
    // Prevent menu from overflowing right edge
    if (left + menuWidth > viewportWidth - 8) { // 8px margin
      left = viewportWidth - menuWidth - 8;
      if (left < 0) left = 0;
    }
    userMenuPosition = { top, left, direction };
    showUserMenu = true;
  }

  function handleUserMenuClose() {
    showUserMenu = false;
    selectedUserForMenu = null;
  }

  // Add click handler to close menu when clicking outside
  function handleClickOutside(event) {
    if (showUserMenu && !event.target.closest('.menu-container')) {
      handleUserMenuClose();
    }
  }

  function handleAuditLogClick(user) {
    auditLogUser = user;
    showAuditLog = true;
  }

  async function openAssignWorkflow(user) {
    try {
      userForAssignment = user;
      showAssignWorkflowModal = true;
      isLoadingAssignments = true;
      assignedMap = {};
      selectedWorkflowIds = new Set();
      // Load active workflows
      const params = new URLSearchParams({ page: '1', pageSize: '500', active: 'true', sortField: 'name', sortOrder: 'asc' });
      const [wfResp, asgResp] = await Promise.all([
        fetch(`${API_BASE_URL}/workflows?${params.toString()}`),
        fetch(`${API_BASE_URL}/userWorkflows?userId=${user._id}&page=1&pageSize=500`)
      ]);
      const wfData = await wfResp.json();
      const asgData = await asgResp.json();
      workflows = wfData?.data || [];
      const assignments = asgData?.data || [];
      for (const a of assignments) {
        const wid = a.workflowId?._id || a.workflowId; // in case populated
        if (wid) {
          assignedMap[wid] = a._id;
          selectedWorkflowIds.add(String(wid));
        }
      }
    } catch (e) {
      console.error('openAssignWorkflow error:', e);
      alert(e.message || 'Failed to load workflows');
    } finally {
      isLoadingAssignments = false;
    }
  }

  function closeAssignWorkflow() {
    showAssignWorkflowModal = false;
    userForAssignment = null;
    assignedMap = {};
    selectedWorkflowIds = new Set();
  }

  function toggleWorkflowSelection(wid) {
    const id = String(wid);
    if (selectedWorkflowIds.has(id)) {
      selectedWorkflowIds.delete(id);
    } else {
      selectedWorkflowIds.add(id);
    }
    // force reactive update
    selectedWorkflowIds = new Set(Array.from(selectedWorkflowIds));
  }

  async function handleAssignWorkflow() {
    if (!userForAssignment?._id) return;
    try {
      isAssigning = true;
      const currentSelected = new Set(Object.keys(assignedMap).map(String));
      const desired = new Set(Array.from(selectedWorkflowIds).map(String));
      const toAssign = Array.from(desired).filter(id => !currentSelected.has(id));
      const toUnassign = Array.from(currentSelected).filter(id => !desired.has(id));

      const requests = [];
      for (const wid of toAssign) {
        requests.push(fetch(`${API_BASE_URL}/userWorkflows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: userForAssignment._id, workflowId: wid })
        }));
      }
      for (const wid of toUnassign) {
        const assignmentId = assignedMap[wid];
        if (assignmentId) {
          requests.push(fetch(`${API_BASE_URL}/userWorkflows/${assignmentId}`, { method: 'DELETE' }));
        }
      }

      const responses = await Promise.all(requests);
      const failures = [];
      for (const r of responses) {
        if (!r.ok) {
          try { failures.push((await r.json())?.message || r.statusText); } catch { failures.push(r.statusText); }
        }
      }
      if (failures.length) {
        alert('Some updates failed: ' + failures.join('; '));
      }
      closeAssignWorkflow();
    } catch (e) {
      console.error('handleAssignWorkflow error:', e);
      alert(e.message || 'Failed to update assignments');
    } finally {
      isAssigning = false;
    }
  }
</script>

<StudyHeader />

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Users" itemCountTitle="Users" itemCount={resultCount}>
  <div class="flex flex-wrap items-center gap-6">
    <!-- Search -->
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          placeholder="Search sponsor, name, email..."
          bind:value={searchText}
          on:keydown={(e) => e.key === 'Enter' && handleSearch()}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8 w-64 lg:w-64"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title="Search">
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>

    <!-- Filters -->
    <div class="flex flex-wrap items-center gap-2">
      <div class="text-m mb-0">
        Country:
        <select
          bind:value={selectedCountry}
          on:change={handleFilterChange}
          class="h-6 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
        >
          <option value="">All Countries</option>
          {#each countryOptions as country}
            <option value={country.value}>{country.label}</option>
          {/each}
        </select>
      </div>

      <div class="text-m mb-0">
        Type:
        <select
          bind:value={selectedUserType}
          on:change={handleFilterChange}
          class="h-6 w-24 lg:w-24 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
        >
          <option value="">All Types</option>
          <option value="Internal">Internal</option>
          <option value="Site">Site</option>
          <option value="CRA">CRA</option>
          <option value="Sponsor">Sponsor</option>
          <option value="Support">Support</option>
        </select>
      </div>

      <div class="text-m mb-0">
        Role:
        <select
          bind:value={selectedRole}
          on:change={handleFilterChange}
          class="h-6 w-36 lg:w-36 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
        >
          <option value="">All Roles</option>
          <option value="admin">Administrator</option>
          <option value="site">Site User</option>
          <option value="study">Study User</option>
          <option value="support">Support User</option>
        </select>
      </div>

      <div class="text-m mb-0">
        State:
        <select
          bind:value={selectedState}
          on:change={handleFilterChange}
          class="h-6 w-24 lg:w-24 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
        >
          <option value="">All States</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="inactive">Blocked</option>
        </select>
      </div>

      <button
        on:click={handleClearFilters}
        class="h-6 px-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600"
        title="Clear all filters"
      >
        Clear
      </button>
    </div>

    <!-- Action buttons -->
    <div class="text-black dark:text-white flex items-center">
      <button 
        class="pagination mr-3 p-0 flex items-center" 
        on:click={handleInviteClick}
        title="Create User"
      >
        <FontAwesomeIcon icon={faUserPlus} />
      </button>
      <button class="pagination mx-1 p-0" on:click={handleRefresh}>
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <div class="overflow-x-auto">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold cursor-pointer w-32" on:click={() => handleSort('sponsor')}>
            Sponsor<span class="text-xs opacity-50">{sponsorSortIndicator}</span>
          </th>
          <th class="px-4 py-2 text-left bold cursor-pointer w-32" on:click={() => handleSort('country')}>
            Country<span class="text-xs opacity-50">{countrySortIndicator}</span>
          </th>
          <th class="px-4 py-2 text-left bold cursor-pointer w-24" on:click={() => handleSort('userType')}>
            Type<span class="text-xs opacity-50">{userTypeSortIndicator}</span>
          </th>
          <th class="px-4 py-2 text-left bold cursor-pointer w-40" on:click={() => handleNameSort()}>
            Name<span class="text-xs opacity-50">{nameSortIndicator}</span>
          </th>
          <th class="px-4 py-2 text-left bold cursor-pointer w-48" on:click={() => handleSort('email')}>
            Email<span class="text-xs opacity-50">{emailSortIndicator}</span>
          </th>
          <th class="px-4 py-2 text-left bold w-32">Role</th>
          <th class="px-4 py-2 text-left bold cursor-pointer w-24" on:click={() => handleSort('lastLogin')}>
            Last Login<span class="text-xs opacity-50">{lastLoginSortIndicator}</span>
          </th>
          <th class="px-4 py-2 text-left bold cursor-pointer w-24" on:click={() => handleSort('state')}>
            State<span class="text-xs opacity-50">{stateSortIndicator}</span>
          </th>
          <th class="w-8 px-4 py-2 text-left bold">&nbsp;</th>
        </tr>
      </thead>

      <tbody>
        {#each users as user, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{user.sponsor ? user.sponsor : '-'}</td>
            <td class="px-4 py-2">{user.country ? user.country : '-'}</td>
            <td class="px-4 py-2">{formatUserTypeDisplay(user.userType)}</td>
            <td class="px-4 py-2">{formatFullName(user.firstName, user.lastName)}</td>
            <td class="px-4 py-2">{user.email ? user.email : ''}</td>
            <td class="px-4 py-2">
              {#if user.roles && user.roles.length > 0}
              {formatRoleDisplay(user.roles[0])}
              {:else}
                <span>-</span>
              {/if}
            </td>
            <td class="px-4 py-2">{user.lastLogin ? formatToLocalDate(user.lastLogin) : 'Never'}</td>
            <td class="px-4 py-2">
              <span
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                class:text-green-700={user.state === 'active'}
                class:ring-green-700={user.state === 'active'}
                class:text-yellow-700={user.state === 'pending'}
                class:ring-yellow-700={user.state === 'pending'}
                class:text-red-500={user.state === 'inactive'}
                class:ring-red-500={user.state === 'inactive'}
                class:text-slate-600={!user.state}
                class:ring-slate-600={!user.state}
              >
                {user.state === 'active' ? 'Active' : user.state === 'pending' ? 'Pending' : user.state === 'inactive' ? 'Blocked' : 'Unknown'}
              </span>
            </td>
            <td class="px-4 py-2 w-8">
              <div class="menu-container relative">
                <button
                  on:click={(e) => handleUserMenuClick(e, user)}
                  class="pagination mx-1 p-0"
                  title="More options"
                >
                  <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                </button>
                {#if showUserMenu && selectedUserForMenu?._id === user._id}
                  <div
                    class="fixed w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-[9999] menu-dropdown"
                    style="top: {userMenuPosition.top}px; left: {userMenuPosition.left}px; max-height: 200px; overflow-y: auto;"
                  >
                    <button
                      on:click={() => {
                        openAssignWorkflow(user);
                        handleUserMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    >
                      <span>Assign Workflow</span>
                      <FontAwesomeIcon icon={faUserTag} class="text-sky-800 dark:text-white/70" />
                    </button>

                    <button
                      on:click={() => {
                        handleEditClick(user);
                        handleUserMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    >
                      <span>Edit user</span>
                      <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                    </button>
                    
                    {#if user.state === 'pending'}
                      <button
                        on:click={() => {
                          handleResendPasswordReset(user);
                          handleUserMenuClose();
                        }}
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                        disabled={isResendingPasswordReset}
                      >
                        <span>Resend password reset</span>
                        <FontAwesomeIcon icon={faEnvelope} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {/if}
                    
                    {#if user.state === 'active' || user.state === 'pending'}
                      <button
                        on:click={() => {
                          handleBlockUser(user);
                          handleUserMenuClose();
                        }}
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      >
                        <span>Block user</span>
                        <FontAwesomeIcon icon={faBan} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {:else if user.state === 'inactive'}
                      <button
                        on:click={() => {
                          handleUnblockUser(user);
                          handleUserMenuClose();
                        }}
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      >
                        <span>Unblock user</span>
                        <FontAwesomeIcon icon={faCheck} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {/if}

                    {#if canViewAuditLogs}
                      <button
                        on:click={() => {
                          handleAuditLogClick(user);
                          handleUserMenuClose();
                        }}
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                      >
                        <span>View audit logs</span>
                        <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
                      </button>
                    {/if}
                  </div>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
        {#if users.length === 0}
          <tr>
            <td colspan="8" class="text-center text-gray-500 dark:text-slate-400 mt-10">No users found</td>
          </tr>
        {/if}
      </tbody>
    </table>
  </div>
</div>

<UserModal
  show={showUserModal}
  user={selectedUser}
  {isCreatingUser}
  on:close={handleUserModalClose}
  on:userUpdated={handleUserUpdated}
  on:userCreated={handleUserCreated}
/>

<ConfirmationModal
  showConfirmation={showConfirmationModal}
  message={confirmationMessage}
  confirmPhrase={confirmationAction === 'block' ? 'block' : 'unblock'}
  on:close={handleConfirmationClose}
  on:confirm={handleConfirmation}
/>

<!-- Audit Log Modal -->
{#if showAuditLog}
  <AuditLogModal
    showModal={showAuditLog}
    documentId={auditLogUser?._id}
    collection="User"
    title={`Audit Logs - ${auditLogUser?.firstName || auditLogUser?.email || 'User'} ${auditLogUser?.lastName || ''}`}
    on:close={() => (showAuditLog = false)}
  />
{/if}

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

<!-- Add loading overlay for export -->
{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

{#if showAssignWorkflowModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={closeAssignWorkflow}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-lg relative z-60" on:click|stopPropagation>
      <header class="flex justify-between items-start mb-3 text-gray-900 dark:text-gray-100">
        <h2 class="text-xl font-bold">Assign Workflows</h2>
        <button class="text-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200" on:click={closeAssignWorkflow}>&times;</button>
      </header>
      <div class="space-y-3">
        {#if isLoadingAssignments}
          <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            Loading…
          </div>
        {:else}
          <div class="max-h-72 overflow-y-auto border rounded dark:border-gray-700">
            {#each workflows as wf}
              <label class="flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0 dark:border-gray-700">
                <span class="text-gray-800 dark:text-gray-200">{wf.name}</span>
                <input type="checkbox" checked={selectedWorkflowIds.has(String(wf._id))} on:change={() => toggleWorkflowSelection(wf._id)} />
              </label>
            {/each}
            {#if (workflows || []).length === 0}
              <div class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No workflows available</div>
            {/if}
          </div>
        {/if}
      </div>
      <div class="flex justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-gray-700 mt-4">
        <button type="button" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700" on:click={closeAssignWorkflow}>Cancel</button>
        <button type="button" class="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700" on:click={handleAssignWorkflow} disabled={isAssigning || isLoadingAssignments}>{isAssigning ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .menu-dropdown {
    transition: none;
  }

  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .fixed {
    position: fixed;
  }

  .inset-0 {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg-opacity-50 {
    --tw-bg-opacity: 0.5;
  }
</style>